import { jsPDF } from 'jspdf';
import { formatDisplayDate } from './dashboardData';
import { formatKilos, formatPercent } from './flujoCalculations';
import { drawPdfReportHeader, loadPdfReportLogo } from './pdfReportHeader';

const orange = [255, 121, 0];
const dark = [21, 25, 29];
const muted = [104, 114, 125];
const light = [248, 249, 250];
const divider = [220, 224, 228];

const number = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const text = (value, fallback = '-') => String(value || fallback);

const drawSectionTitle = (doc, title, y) => {
  doc.setTextColor(...orange);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text(title, 14, y);
  doc.setDrawColor(...divider);
  doc.line(14, y + 2.5, 196, y + 2.5);
};

const drawField = (doc, label, value, x, y, width = 55) => {
  doc.setTextColor(...muted);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.text(label, x, y);
  doc.setTextColor(...dark);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  const lines = doc.splitTextToSize(text(value), width);
  doc.text(lines.slice(0, 2), x, y + 5);
};

const getClassificationRows = (entrada, totals) => [
  ['Precalibrado', entrada.otrosCalibres, entrada.pOtrosCalibres, totals.detalle.otrosCalibresKg, totals.percentages.otrosCalibres],
  ['Retorno', entrada.retorno, entrada.pRetorno, totals.detalle.retornoKg, totals.percentages.retorno],
  ['Verde', entrada.verde, entrada.pVerde, totals.detalle.verdeKg, totals.percentages.verde],
  ['Industria', entrada.industria, entrada.pIndustria, totals.detalle.industriaKg, totals.percentages.industria],
  ['Podrido', entrada.podrido, null, totals.detalle.podridoKg, totals.percentages.podrido],
  ['Primera (calculada)', null, null, totals.kilosPrimera, totals.percentages.primera],
];

const drawClassificationTable = (doc, entrada, totals, y) => {
  const columns = [
    { label: 'Tipo', x: 17, align: 'left' },
    { label: 'Kilos introducidos', x: 108, align: 'right' },
    { label: 'Palots', x: 137, align: 'right' },
    { label: 'Total calculado', x: 170, align: 'right' },
    { label: '%', x: 193, align: 'right' },
  ];

  doc.setFillColor(...light);
  doc.rect(14, y, 182, 9, 'F');
  doc.setTextColor(...muted);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  columns.forEach((column) => doc.text(column.label, column.x, y + 5.8, { align: column.align }));

  getClassificationRows(entrada, totals).forEach((row, index) => {
    const rowY = y + 9 + index * 7;

    if (index % 2 === 1) {
      doc.setFillColor(252, 252, 253);
      doc.rect(14, rowY, 182, 7, 'F');
    }

    doc.setTextColor(...dark);
    doc.setFont('helvetica', row[0] === 'Primera (calculada)' ? 'bold' : 'normal');
    doc.setFontSize(8);
    doc.text(row[0], 17, rowY + 4.8);
    doc.text(row[1] === null ? '-' : formatKilos(number(row[1])), 108, rowY + 4.8, { align: 'right' });
    doc.text(row[2] === null ? '-' : formatKilos(number(row[2])), 137, rowY + 4.8, { align: 'right' });
    doc.text(`${formatKilos(number(row[3]))} kg`, 170, rowY + 4.8, { align: 'right' });
    doc.text(`${formatPercent(number(row[4]))} %`, 193, rowY + 4.8, { align: 'right' });
  });
};

const drawSummary = (doc, totals, y) => {
  const items = [
    ['Kg volcados', `${formatKilos(totals.kilosVolcados)} kg`],
    ['Kg reales', `${formatKilos(totals.kgReales)} kg`],
    ['Kg clasificados', `${formatKilos(totals.kilosClasificados)} kg`],
    ['Sin clasificar', `${formatKilos(totals.kilosSinClasificar)} kg`],
    ['Rendimiento', `${formatPercent(totals.rendimiento)} %`],
    ['Total palots', formatKilos(totals.totalPalots)],
  ];

  const itemWidth = 58;
  items.forEach(([label, value], index) => {
    const row = Math.floor(index / 3);
    const column = index % 3;
    const x = 14 + column * 62;
    const itemY = y + row * 19;

    doc.setFillColor(...light);
    doc.roundedRect(x, itemY, itemWidth, 15, 1.5, 1.5, 'F');
    doc.setTextColor(...muted);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.text(label, x + 3, itemY + 5);
    doc.setTextColor(...dark);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text(value, x + 3, itemY + 11.5);
  });
};

export const downloadEntradaPreviewPdf = async ({
  entrada,
  totals,
  almacenes = [],
  variedades = [],
  cajasPalet = [],
}) => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const logoDataUrl = await loadPdfReportLogo();
  const almacen = almacenes.find((item) => item.id === Number(entrada.idAlmacen));
  const variedad = variedades.find((item) => String(item.id) === String(entrada.idVariedad));
  const isSavedEntry = entrada.codigo !== null && entrada.codigo !== undefined && entrada.codigo !== '';

  drawPdfReportHeader(doc, {
    logoDataUrl,
    pageNumber: 1,
    rightDetail: isSavedEntry ? `Entrada registrada #${entrada.codigo}` : 'Documento previo al guardado',
    subtitle: `Fecha de entrada: ${formatDisplayDate(entrada.fecha) || '-'}`,
    title: 'FICHA DE ENTRADA DE FRUTA',
    totalPages: 1,
  });

  drawSectionTitle(doc, 'DATOS GENERALES', 34);
  const generalFields = [
    ...(isSavedEntry ? [['Codigo', `#${entrada.codigo}`]] : []),
    ['Fecha', formatDisplayDate(entrada.fecha)],
    ['Almacen', almacen?.nombre || entrada.almacen || 'Sin seleccionar'],
    ['Variedad', variedad?.nombre || entrada.variedad || 'Sin seleccionar'],
  ];
  const generalFieldWidth = 180 / generalFields.length;
  generalFields.forEach(([label, value], index) => {
    drawField(doc, label, value, 17 + index * generalFieldWidth, 41, generalFieldWidth - 7);
  });

  drawSectionTitle(doc, 'KILOS VOLCADOS', 59);
  drawField(doc, 'Kilos directos', `${formatKilos(number(entrada.kilos))} kg`, 17, 66, 36);
  drawField(doc, 'Grupos de 4 palots', formatKilos(number(entrada.crucesPalots)), 58, 66, 36);
  drawField(doc, 'Peso del palot', `${formatKilos(number(entrada.kilosPalot))} kg`, 101, 66, 36);
  drawField(doc, 'Peso de caja', `${formatKilos(number(entrada.pesoCaja))} kg`, 144, 66, 36);

  const paletSummary = cajasPalet
    .map((item) => `${item.cajas} cajas: ${formatKilos(number(entrada.paletsEntrada?.[item.id]))}`)
    .join('   |   ');
  drawField(doc, 'Palets por tipo', paletSummary || 'Sin tipos de palet', 17, 82, 160);

  drawSectionTitle(doc, 'CLASIFICACION DE FRUTA', 101);
  drawClassificationTable(doc, entrada, totals, 106);

  drawSectionTitle(doc, 'RESUMEN CALCULADO', 178);
  drawSummary(doc, totals, 184);

  drawSectionTitle(doc, 'OBSERVACIONES', 228);
  doc.setTextColor(...dark);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  const observations = text(entrada.observaciones, 'Sin observaciones');
  doc.text(doc.splitTextToSize(observations, 176).slice(0, 6), 17, 236);

  const filenameDate = entrada.fecha || new Date().toISOString().slice(0, 10);
  const filenamePrefix = isSavedEntry ? `entrada-${entrada.codigo}` : 'entrada-previa';
  doc.save(`${filenamePrefix}-${filenameDate}.pdf`);
};
